import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Heart } from 'lucide-react';
import { getObraImageUrl } from '@/utils/image-utils';
import { ObraCoverImage } from '@/components/obra/ObraCoverImage';
import { generateWorkLink } from '@/utils/utils';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import type { ReactNode } from 'react';

export interface MinimalistWorkCardObra {
    id: number;
    nome: string;
    imagem?: string;
    favoritada?: boolean;
    total_capitulos?: number;
}

interface MinimalistWorkCardProps {
    obra: MinimalistWorkCardObra;
    viewMode?: 'grid' | 'list';
    showFavoriteButton?: boolean;
    topRightAction?: ReactNode;
    /** Etiqueta de formato no topo da capa (ex.: pesquisa com filtro «Todos»). */
    formatoBadge?: { label: string; color: string };
}

export default function MinimalistWorkCard({
    obra,
    viewMode = 'grid',
    showFavoriteButton = true,
    topRightAction,
    formatoBadge,
}: MinimalistWorkCardProps) {
    const { isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    const [isFavoritadaLocal, setIsFavoritadaLocal] = useState(obra.favoritada || false);
    const [isLoadingFavorite, setIsLoadingFavorite] = useState(false);

    const totalCaps = obra.total_capitulos || 0;

    const handleToggleFavorite = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (!isAuthenticated || !user?.usr_id) {
            return navigate('/entrar');
        }

        if (isLoadingFavorite) return;

        try {
            setIsLoadingFavorite(true);
            const newStatus = !isFavoritadaLocal;
            setIsFavoritadaLocal(newStatus);
            await api.post(`favorites/toggle/${obra.id}`, {});
        } catch (error) {
            console.error('Erro ao favoritar/desfavoritar:', error);
            setIsFavoritadaLocal(obra.favoritada || false);
        } finally {
            setIsLoadingFavorite(false);
        }
    };

    const imageWrapClass =
        viewMode === 'grid'
            ? 'relative w-full aspect-[3/4] flex-shrink-0 overflow-hidden rounded-xl border transition-all duration-500 group-hover:scale-[1.02]'
            : 'relative w-24 md:w-28 aspect-[3/4] flex-shrink-0 overflow-hidden rounded-xl border transition-all duration-500 group-hover:scale-[1.02]';

    const titleClass =
        viewMode === 'grid'
            ? 'font-semibold text-white text-sm leading-tight line-clamp-2 group-hover:text-white transition-colors'
            : 'font-semibold text-white text-base md:text-lg leading-tight line-clamp-2 group-hover:text-white transition-colors';

    return (
        <div
            className={`group flex transition-all duration-500 ${viewMode === 'grid' ? 'flex-col gap-2.5' : 'flex-row gap-4'}`}
        >
            <Link
                to={generateWorkLink(obra.id)}
                className={`${imageWrapClass} ${
                    isFavoritadaLocal
                        ? 'bg-yellow-500/5 border-yellow-500/40'
                        : 'bg-white/[0.03] border-white/10 group-hover:border-white/20'
                }`}
                style={{
                    boxShadow: isFavoritadaLocal
                        ? '0 10px 30px -10px rgba(234, 179, 8, 0.2)'
                        : '0 10px 30px -10px rgba(0,0,0,0.5)',
                }}
            >
                <ObraCoverImage
                    src={getObraImageUrl(obra.id, obra.imagem, 600) || obra.imagem || '/placeholder-book.jpg'}
                    alt={obra.nome}
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />

                {formatoBadge ? (
                    <div
                        className="absolute top-2 left-2 z-[15] max-w-[calc(100%-3.5rem)] truncate rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-tight backdrop-blur-md shadow-lg"
                        style={{
                            borderColor: formatoBadge.color,
                            color: formatoBadge.color,
                            backgroundColor: `${formatoBadge.color}26`,
                        }}
                    >
                        {formatoBadge.label}
                    </div>
                ) : null}

                {totalCaps > 0 && (
                    <div className="absolute bottom-2 left-2 z-[12] flex items-center gap-1 rounded-lg border border-white/10 bg-black/70 px-2 py-1 text-white shadow-lg backdrop-blur-md transition-transform duration-300 group-hover:scale-105">
                        <BookOpen size={10} className="text-orange-500" />
                        <span className="text-[10px] font-black uppercase tracking-tighter">
                            {totalCaps} {totalCaps === 1 ? 'Cap' : 'Caps'}
                        </span>
                    </div>
                )}

                {topRightAction}

                {showFavoriteButton && !topRightAction && (
                    <button
                        onClick={handleToggleFavorite}
                        disabled={isLoadingFavorite}
                        className={`absolute top-2 right-2 p-2 rounded-full backdrop-blur-md border transition-all z-20 hover:scale-110 active:scale-95 ${
                            isFavoritadaLocal
                                ? 'bg-yellow-500/20 border-yellow-500/40 text-yellow-500'
                                : 'bg-black/40 border-white/10 text-white/70 hover:text-white hover:bg-black/60'
                        }`}
                    >
                        <Heart className={`w-3.5 h-3.5 ${isFavoritadaLocal ? 'fill-yellow-500' : 'fill-transparent'}`} />
                    </button>
                )}
            </Link>

            <div className={`flex-1 flex flex-col min-w-0 ${viewMode === 'grid' ? 'mt-0.5' : 'justify-center py-1'}`}>
                <Link to={generateWorkLink(obra.id)} className={viewMode === 'grid' ? 'block h-[2.5rem] overflow-hidden' : undefined}>
                    <h3 className={titleClass} title={obra.nome}>
                        {obra.nome}
                    </h3>
                </Link>
            </div>
        </div>
    );
}
